import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    FileText, Upload, Loader2, Copy, Check, Download,
    ScanLine, ArrowRightLeft, FileType,
} from 'lucide-react';
import { toast } from '../ui/Toast';
import * as documentsApi from '../../services/documentsApi';
import { DocumentsApiError } from '../../services/documentsApi';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // fallback si API indisponible

const PALETTE_OPTIONS = [
    { value: 'scanner-le-sens', label: 'Scanner le Sens / QIMI 7 (défaut)' },
    { value: 'bleu', label: 'Bleu' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'editorial', label: 'Éditorial' },
    { value: 'recherche', label: 'Recherche' },
    { value: 'technique', label: 'Technique' },
    { value: 'premium-dark', label: 'Premium sombre' },
    { value: 'academic-light', label: 'Académique clair' },
];

const palette = {
    accent: '#22c55e',
    accentBg: 'rgba(34,197,94,0.12)',
    accentBorder: 'rgba(34,197,94,0.30)',
};

const TABS = [
    {
        id: 'ocr',
        label: 'PDF scanné → OCR',
        icon: ScanLine,
        accept: '.pdf,application/pdf',
        hint: 'Glissez un PDF scanné ici',
        validate: (f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf',
        validateMsg: 'Seuls les fichiers PDF sont acceptés',
        to: 'searchable_pdf',
        from: 'pdf',
        buttonLabel: "Lancer l'OCR",
        processingLabel: 'OCR en cours…',
        doneToast: 'OCR terminé',
        durationHint: 'Peut prendre 1 à 3 min selon la taille du document',
    },
    {
        id: 'pdf2md',
        label: 'PDF → Markdown',
        icon: ArrowRightLeft,
        accept: '.pdf,application/pdf',
        hint: 'Glissez un PDF avec couche texte',
        validate: (f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf',
        validateMsg: 'Seuls les fichiers PDF sont acceptés',
        to: 'markdown',
        from: 'pdf',
        buttonLabel: 'Convertir en Markdown',
        processingLabel: 'Extraction en cours…',
        doneToast: 'Markdown généré',
        durationHint: 'Quelques secondes pour un PDF texte',
    },
    {
        id: 'md2pdf',
        label: 'Markdown → PDF',
        icon: FileType,
        accept: '.md,.markdown,.txt,text/markdown,text/plain',
        hint: 'Glissez un fichier .md ou .txt',
        validate: (f) => /\.(md|markdown|txt)$/i.test(f.name)
            || f.type === 'text/markdown' || f.type === 'text/plain',
        validateMsg: 'Formats acceptés : .md, .markdown, .txt',
        to: 'pdf',
        from: 'markdown',
        buttonLabel: 'Générer le PDF',
        processingLabel: 'Génération PDF…',
        doneToast: 'PDF généré',
        durationHint: 'Génération locale — quelques secondes',
    },
];

export default function DocumentsPage() {
    const [activeTab, setActiveTab] = useState('ocr');
    const [file, setFile] = useState(null);
    const [toFmt, setToFmt] = useState('searchable_pdf');
    const [mdPalette, setMdPalette] = useState('scanner-le-sens');
    const [limits, setLimits] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [job, setJob] = useState(null);
    const [copied, setCopied] = useState(false);
    const fileInputRef = useRef(null);
    const dropRef = useRef(null);

    const tab = TABS.find((t) => t.id === activeTab) || TABS[0];

    useEffect(() => {
        documentsApi.getLimits()
            .then((data) => setLimits(data.limits))
            .catch(() => setLimits(null));
    }, []);

    const maxFileSize = limits?.max_file_size_mb
        ? limits.max_file_size_mb * 1024 * 1024
        : MAX_FILE_SIZE;

    const switchTab = (tabId) => {
        setActiveTab(tabId);
        setFile(null);
        setJob(null);
        const next = TABS.find((t) => t.id === tabId);
        if (next?.id === 'ocr') setToFmt('searchable_pdf');
    };

    const handleFileSelect = (f) => {
        if (!f) return;
        if (!tab.validate(f)) {
            toast.error(tab.validateMsg);
            return;
        }
        if (f.size > maxFileSize) {
            toast.error(`Fichier trop volumineux (max ${limits?.max_file_size_mb || 50} Mo)`);
            return;
        }
        setFile(f);
        setJob(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropRef.current) dropRef.current.style.borderColor = 'var(--card-border)';
        if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
    };

    const handleConvert = useCallback(async () => {
        if (!file || processing) return;
        setProcessing(true);
        setJob(null);
        try {
            const opts = {
                from: tab.from,
                to: tab.id === 'ocr' ? toFmt : tab.to,
            };
            if (tab.id === 'ocr') opts.language = 'fra+eng';
            if (tab.id === 'md2pdf') opts.palette = mdPalette;

            const created = await documentsApi.createConvertJob(file, opts);
            const finalJob = await documentsApi.pollJob(created.job_id, {
                onTick: (j) => setJob(j),
            });
            setJob(finalJob);
            if (finalJob.status === 'error') {
                toast.error(finalJob.error || 'Erreur conversion');
            } else {
                toast.success(tab.doneToast);
            }
        } catch (err) {
            const msg = err instanceof DocumentsApiError ? err.message : err.message;
            toast.error(msg || 'Erreur conversion');
        } finally {
            setProcessing(false);
        }
    }, [file, toFmt, mdPalette, processing, tab]);

    const handleCopy = () => {
        if (!job?.text) return;
        navigator.clipboard.writeText(job.text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownload = async (format) => {
        const dlPath = job?.download_paths?.[format];
        if (!dlPath) return;
        try {
            const url = await documentsApi.signDownloadUrl(dlPath);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            toast.error('Impossible de générer le lien de téléchargement');
        }
    };

    const statusLabel = () => {
        if (!job) return 'Démarrage…';
        if (job.status === 'processing') {
            if (activeTab === 'ocr') return 'Traitement OCR…';
            if (activeTab === 'pdf2md') return 'Extraction texte…';
            return 'Génération PDF…';
        }
        return 'Démarrage…';
    };

    const engineLabel = () => {
        if (activeTab === 'ocr') return 'OCR souverain (ocrmypdf)';
        if (activeTab === 'pdf2md') return 'Extraction souveraine (pdf-parse)';
        return 'PDF premium (pdfkit)';
    };

    return (
        <div style={{ height: '100%', overflowY: 'auto' }}>
            <div style={{ padding: '32px 40px 56px', maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 10,
                        background: `linear-gradient(135deg, ${palette.accentBg}, rgba(34,197,94,0.05))`,
                        border: `1px solid ${palette.accentBorder}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <FileText size={20} style={{ color: palette.accent }} />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
                            Documents
                        </h1>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                            Usine à conversion — OCR, PDF↔Markdown (souverain)
                            {limits && (
                                <span style={{ marginLeft: 8, opacity: 0.85 }}>
                                    · max {limits.max_file_size_mb} Mo · {limits.max_pdf_pages} pages PDF · aperçu {Math.round(limits.preview_max_chars / 1000)}k car.
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Onglets */}
                <div style={{
                    marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap',
                }}>
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => switchTab(t.id)}
                                disabled={processing}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    padding: '10px 16px', borderRadius: 10, cursor: processing ? 'not-allowed' : 'pointer',
                                    border: active ? `1px solid ${palette.accentBorder}` : '1px solid var(--card-border)',
                                    background: active ? palette.accentBg : 'var(--card-bg)',
                                    color: active ? palette.accent : 'var(--text-secondary)',
                                    fontWeight: active ? 600 : 500, fontSize: 13,
                                }}
                            >
                                <Icon size={16} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                <div style={{ marginTop: 28, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {/* Carte conversion */}
                    <div style={{
                        flex: '1 1 420px', background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)', borderRadius: 14, padding: 24,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                            <tab.icon size={18} style={{ color: palette.accent }} />
                            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                                {tab.label}
                            </span>
                        </div>

                        {activeTab === 'ocr' && (
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Résultat souhaité</label>
                                <select
                                    value={toFmt}
                                    onChange={(e) => setToFmt(e.target.value)}
                                    disabled={processing}
                                    style={selectStyle}
                                >
                                    <option value="searchable_pdf">PDF cherchable (recommandé)</option>
                                    <option value="text">Texte brut uniquement</option>
                                </select>
                            </div>
                        )}

                        {activeTab === 'md2pdf' && (
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Palette PDF</label>
                                <select
                                    value={mdPalette}
                                    onChange={(e) => setMdPalette(e.target.value)}
                                    disabled={processing}
                                    style={selectStyle}
                                >
                                    {PALETTE_OPTIONS.map((p) => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {activeTab === 'pdf2md' && (
                            <div style={{
                                marginBottom: 14, padding: '10px 12px', borderRadius: 8,
                                background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
                                fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5,
                            }}>
                                Pour les PDF scannés sans couche texte, utilisez l&apos;onglet OCR puis copiez le texte extrait.
                            </div>
                        )}

                        <div
                            ref={dropRef}
                            onDragOver={(e) => { e.preventDefault(); dropRef.current.style.borderColor = palette.accent; }}
                            onDragLeave={(e) => { e.preventDefault(); dropRef.current.style.borderColor = 'var(--card-border)'; }}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                border: '2px dashed var(--card-border)', borderRadius: 12,
                                padding: '32px 16px', textAlign: 'center', cursor: 'pointer',
                                transition: 'border-color 0.2s',
                            }}
                        >
                            <Upload size={28} style={{ color: 'var(--text-secondary)', marginBottom: 8 }} />
                            <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>
                                {tab.hint}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                                ou cliquez pour parcourir (max {limits?.max_file_size_mb || 50} Mo)
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={tab.accept}
                                hidden
                                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                            />
                        </div>

                        {file && (
                            <div style={{
                                marginTop: 12, padding: '10px 12px', borderRadius: 8,
                                background: 'rgba(34,197,94,0.08)', border: `1px solid ${palette.accentBorder}`,
                                fontSize: 13, color: 'var(--text)',
                            }}>
                                {file.name} — {(file.size / 1024 / 1024).toFixed(1)} Mo
                            </div>
                        )}

                        <button
                            onClick={handleConvert}
                            disabled={!file || processing}
                            style={{
                                marginTop: 16, width: '100%', padding: '14px 20px', borderRadius: 12,
                                border: 'none', cursor: !file || processing ? 'not-allowed' : 'pointer',
                                background: !file || processing ? 'var(--card-border)' : 'linear-gradient(135deg, #16a34a, #22c55e)',
                                color: '#fff', fontWeight: 600, fontSize: 15,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            }}
                        >
                            {processing ? (
                                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> {tab.processingLabel}</>
                            ) : (
                                <><tab.icon size={18} /> {tab.buttonLabel}</>
                            )}
                        </button>

                        {processing && (
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, textAlign: 'center' }}>
                                {tab.durationHint}
                            </div>
                        )}
                    </div>

                    {/* Résultat */}
                    <div style={{
                        flex: '1 1 420px', background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)', borderRadius: 14, padding: 24,
                        minHeight: 320, display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                            Résultat
                        </div>

                        {!job && !processing && (
                            <div style={{
                                flex: 1, display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center', opacity: 0.45, gap: 10,
                            }}>
                                <FileText size={40} style={{ color: 'var(--text-secondary)' }} />
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center' }}>
                                    Le texte extrait et les fichiers convertis apparaîtront ici
                                </div>
                            </div>
                        )}

                        {processing && !job?.text && (
                            <div style={{
                                flex: 1, display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center', gap: 12,
                            }}>
                                <Loader2 size={32} style={{ color: palette.accent, animation: 'spin 1s linear infinite' }} />
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                    {statusLabel()}
                                </div>
                            </div>
                        )}

                        {job?.status === 'error' && (
                            <div style={{ color: '#ef4444', fontSize: 14 }}>{job.error}</div>
                        )}

                        {job?.status === 'done' && (
                            <>
                                {(job.preview_truncated || job.meta?.pages_truncated || job.meta?.text_truncated) && (
                                    <div style={{
                                        marginBottom: 12, padding: '10px 12px', borderRadius: 8,
                                        background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
                                        fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5,
                                    }}>
                                        {job.meta?.pages_truncated && (
                                            <div>Extraction limitée à {job.meta.pages_extracted} pages sur {job.meta.numpages} — téléchargez le Markdown complet.</div>
                                        )}
                                        {job.meta?.text_truncated && (
                                            <div>Texte source tronqué au plafond serveur — le fichier téléchargé peut être incomplet.</div>
                                        )}
                                        {job.preview_truncated && (
                                            <div>Aperçu tronqué ({limits?.preview_max_chars?.toLocaleString('fr-FR') || '12 000'} car. max) — téléchargez le fichier pour le contenu intégral.</div>
                                        )}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                                    {job.download_paths?.searchable_pdf && (
                                        <button onClick={() => handleDownload('searchable_pdf')} style={actionBtnStyle}>
                                            <Download size={14} /> PDF cherchable
                                        </button>
                                    )}
                                    {job.download_paths?.pdf && (
                                        <button onClick={() => handleDownload('pdf')} style={actionBtnStyle}>
                                            <Download size={14} /> PDF généré
                                        </button>
                                    )}
                                    {job.download_paths?.markdown && (
                                        <button onClick={() => handleDownload('markdown')} style={actionBtnStyle}>
                                            <Download size={14} /> Fichier Markdown
                                        </button>
                                    )}
                                    {job.download_paths?.text && (
                                        <button onClick={() => handleDownload('text')} style={actionBtnStyle}>
                                            <Download size={14} /> Fichier texte
                                        </button>
                                    )}
                                    {job.text && (
                                        <button onClick={handleCopy} style={actionBtnStyle}>
                                            {copied ? <><Check size={14} /> Copié</> : <><Copy size={14} /> Copier le texte</>}
                                        </button>
                                    )}
                                </div>
                                {job.text ? (
                                    <div style={{
                                        flex: 1, overflow: 'auto', fontSize: 14, lineHeight: 1.7,
                                        color: 'var(--text)', whiteSpace: 'pre-wrap',
                                        background: 'var(--input-bg)', borderRadius: 10, padding: 16,
                                        border: '1px solid var(--card-border)',
                                    }}>
                                        {job.text}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                        Conversion terminée — téléchargez le fichier ci-dessus.
                                    </div>
                                )}
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8, textAlign: 'right' }}>
                                    {job.text ? `${job.text.length.toLocaleString('fr-FR')} car. aperçu — ` : ''}
                                    {job.meta?.numpages ? `${job.meta.numpages} pages · ` : ''}
                                    {job.meta?.palette ? `palette ${job.meta.palette} · ` : ''}
                                    {engineLabel()}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

const labelStyle = {
    display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text)',
};

const selectStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: '1px solid var(--card-border)', background: 'var(--input-bg)',
    color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box',
    cursor: 'pointer', appearance: 'auto',
};

const actionBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--card-border)', background: 'transparent',
    color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12,
};
